import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ContentChild
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AbstractValueAccessor, MakeProvider } from '@anecdotes/design-system/form-controls';
import { MenuAction } from '../../types';
import { leaveOneSpace } from '@anecdotes/utils';
import { filter } from 'rxjs/operators';
import { CdkConnectedOverlay, ConnectedPosition } from '@angular/cdk/overlay';
import { BehaviorSubject } from 'rxjs';
import { PlacementArray } from '@ng-bootstrap/ng-bootstrap/util/positioning';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-dropdown-control',
  templateUrl: './dropdown-control.component.html',
  styleUrls: ['./dropdown-control.component.scss'],
  providers: [MakeProvider(DropdownControlComponent)],
  standalone: false,
})
export class DropdownControlComponent extends AbstractValueAccessor implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('contentContainer')
  private contentContainer: ElementRef<HTMLElement>;

  @ViewChild('triggerButton', { read: ElementRef })
  private triggerButton: ElementRef<HTMLElement>;

  @HostBinding('class')
  private readonly hostClasses = 'block';

  private openableElementElementRef: ElementRef<HTMLElement>;

  private previousVisibleItemsCount: number;

  @ViewChildren('openableElement', { read: ElementRef })
  private openableElementQueryList: QueryList<ElementRef<HTMLElement>>;

  @ContentChild('dropdownTrigger')
  protected customTriggerContent: TemplateRef<any>;

  @ViewChild(CdkConnectedOverlay, { static: true })
  private cdkConnectedOverlay: CdkConnectedOverlay;

  @HostBinding('class.disabled')
  get disabled(): boolean {
    return this.isDisabled || !this.data;
  }

  readonly addOptionLoading$ = new BehaviorSubject<boolean>(false);

  readonly buttonHeight: number = 40;

  readonly selectItemFunc = this.selectItemClick.bind(this);

  readonly searchField = new UntypedFormControl('');

  displayedData: any[];

  isDropdownOpened = false;

  bottomDropdownDisplayed: boolean;

  searchValue: string;

  isAddingShown = false;

  private get hostInlinePaddingsWidth(): number {
    const hostComputedStyle = getComputedStyle(this.hostElementRef.nativeElement);
    const paddingLeft = parseFloat(hostComputedStyle.getPropertyValue('padding-left'));
    const paddingRight = parseFloat(hostComputedStyle.getPropertyValue('padding-right'));

    return paddingLeft + paddingRight;
  }

  get hasCustomErrors(): boolean {
    return this.errorsDisplayed
      && !!this.errorTexts
      && Object.keys(this.errors).some((error) => error in this.errorTexts);
  }

  get errors(): Record<string, boolean> {
    return this.formControl?.errors || {};
  }

  get listWidth(): number {
    if (!this.isDropdownOpened) {
      return 0;
    }

    switch (this.customListWidth) {
      case 'fit-dropdown-button':
        return this.hostElementRef.nativeElement.getBoundingClientRect().width - this.hostInlinePaddingsWidth;
      case 'double-dropdown-button':
        return this.hostElementRef.nativeElement.getBoundingClientRect().width * 2 - this.hostInlinePaddingsWidth;
      default:
        if (typeof this.customListWidth === 'number') {
          return this.customListWidth;
        }
        return this.listMinWidth;
    }
  }

  get listMaxHeight(): number {
    if (this.previousVisibleItemsCount !== this.visibleItemsCount) {
      return this.visibleItemsCount * this.buttonHeight;
    }

    return this.previousVisibleItemsCount;
  }

  get contentExists(): boolean {
    return this.contentContainer && !!this.contentContainer.nativeElement.childNodes.length;
  }

  get dropdownOptionHeight(): string {
    return this.optionHeight || `${this.buttonHeight}px`;
  }

  get responsiveOverlayPositions(): ConnectedPosition[] {
    if (!this.triggerButton) {
      return;
    }

    const positionStartBottom: ConnectedPosition = {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    };
    const positionEndBottom: ConnectedPosition = {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    };
    const positionStartTop: ConnectedPosition = {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
    };
    const positionEndTop: ConnectedPosition = {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    };

    this.cdkConnectedOverlay?.overlayRef?.updatePosition();

    return [positionStartBottom, positionEndBottom, positionStartTop, positionEndTop];
  }

  get isAddOptionShown(): boolean {
    return this.addingEnabled && this.searchField.value && this.isAddingShown;
  }

  get hasSelectedOptionIcon(): boolean {
    return this.showSelectedOptionIcon && this.value?.icon && !this.loading;
  }

  @Input()
  data: any[];

  @Input()
  displayValueSelector: (item: any) => string;

  @Input()
  displayValueButton: (item: any) => string;

  @Input()
  disableValueSelector: (item: any) => boolean;

  @Input()
  idSelector?: (item: any) => string | number;

  @Input()
  itemHoverTooltip: {
    displayCondition: (item: any) => boolean;
    templateTooltip: TemplateRef<any>;
  };

  @Input()
  context: any;

  @Input()
  titleTranslationKey: string;

  @Input()
  customListWidth?: 'fit-dropdown-button' | 'double-dropdown-button' | 'responsive' | number = 'fit-dropdown-button';

  @Input()
  titleWithColon = true;

  @Input()
  customTriggerTemplate: TemplateRef<any>;

  @Input()
  controlTooltip?: string | TemplateRef<any>;

  @Input()
  controlTooltipPlacement: PlacementArray = 'right';

  @Input()
  infoTooltip: string | TemplateRef<any>;

  @Input()
  placeholderTranslationKey: string;

  @Input()
  searchEnabled = false;

  @Input()
  searchValueSelector: (item: any) => string;

  @Input()
  required: boolean;

  @Input()
  searchFieldPlaceholder: string;

  @Input()
  searchAutoFocus = false;

  @Input()
  visibleItemsCount = 4;

  @Input()
  overlayPanelClass = 'dropdown-control-overlay';

  @Input()
  comingSoon = false;

  @Input()
  loading: boolean;

  @Input()
  selectFirstValue = true;

  @Input()
  bottomDropdownAction: MenuAction;

  @Input()
  unselectOption = false;

  @Input()
  listMinWidth: number;

  @Input()
  selectedValue: any;

  @Input()
  emptyListPlaceholder = 'core.dropdown.emptyListPlaceholder';

  @Input()
  labelIcon: string;

  @Input()
  optionHeight: string;

  @Input()
  customDropdownOptionRef?: TemplateRef<any>;

  @Input()
  doesOptionShouldBeShownFully = false;

  @Input()
  inputDescriptionTranslationKey: string;

  @Input()
  dropdownPosition: ConnectedPosition[];

  @Input()
  disableRepeatSelectionEvent: boolean;

  @Input()
  addingEnabled = false;

  @Input()
  shouldDisableAddOptionHandler: (value: string) => boolean;

  @Input()
  entityNameText: string;

  @Input()
  addEntityCallback: (searchValue: string) => unknown;

  @Input()
  showSelectedOptionIcon: boolean;

  @Input()
  dataTestId: string;

  @Input()
  disabledFilterState? = false;

  @Input()
  shouldHandleOutsideClick = true;

  @Input()
  fixedIconSize = false;

  @Output()
  // eslint-disable-next-line @angular-eslint/no-output-native
  select = new EventEmitter<any>(true);

  @Output()
  // eslint-disable-next-line @angular-eslint/no-output-native
  selectClick = new EventEmitter<any>(true);

  @Output()
  addNew = new EventEmitter<string>();

  @Output()
  dropdownOpen = new EventEmitter<void>();

  constructor(
    injector: Injector,
    protected translate: TranslateService,
    protected hostElementRef: ElementRef<HTMLElement>,
    protected cd: ChangeDetectorRef,
  ) {
    super(injector);
  }

  ngAfterViewInit(): void {
    this.openableElementQueryList.notifyOnChanges();

    this.openableElementQueryList.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => (this.openableElementElementRef = this.openableElementQueryList.first));
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.bottomDropdownDisplayed = !this.bottomDropdownAction?.showOnlyOnSearch;

    if (this.bottomDropdownAction?.shouldResetOnClick) {
      const action = this.bottomDropdownAction.action;
      this.bottomDropdownAction.action = (context) => this.handleBottomActionClick(action, context);
    }

    this.searchField.valueChanges
      .pipe(
        filter(() => !!this.data),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value: string) => this.search(value));
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if ('data' in changes && this.data) {
      this.displayedData = this.data;

      if (this.selectFirstValue && !this.value && !this.placeholderTranslationKey && this.data.length) {
        this.selectItem(this.data[0]);
      }
    }

    if ('selectFirstValue' in changes && this.selectFirstValue && !this.value && this.data?.length) {
      this.selectItem(this.data[0]);
    }

    if (
      'selectedValue' in changes &&
      this.selectedValue &&
      this.displayedData?.find((value) => this.getItemId(value) === this.getItemId(this.selectedValue))
    ) {
      this.selectItem(this.displayedData.find((value) => this.getItemId(value) === this.getItemId(this.selectedValue)));
    }
  }

  handleBottomActionClick(action: any, context: any): void {
    action(context);

    this.resetDropdown();
  }

  selectItemClick(selectedValue: any): void {
    if (this.compareItemToCurrentValue(selectedValue) && this.disableRepeatSelectionEvent) {
      return;
    }

    // Should emit event before selectItem function executes
    this.selectClick.emit(selectedValue);
    this.selectItem(selectedValue);
  }

  compareItemToCurrentValue(selectedValue: any): boolean {
    return this.getItemId(selectedValue) === this.getItemId(this.value);
  }

  selectItem(selectedValue: any): void {
    this.value = selectedValue;
    this.select.emit(selectedValue);
    this.resetDropdown();
  }

  resetDropdown(): void {
    this.searchField.setValue('');
    this.displayedData = this.data;
    this.isDropdownOpened = false;
    this.cd.detectChanges();
  }

  search(value: string): void {
    const searchValue = leaveOneSpace(value);

    this.displayedData = this.data.filter((item) => {
      const itemLabel = this.searchValueSelector ? this.searchValueSelector(item) : this.getDisplayValue(item);
      const instant = this.translate.instant(itemLabel);
      let resolvedTranslation: string = itemLabel;
      if (instant && typeof instant === 'string') {
        resolvedTranslation = instant;
      }

      return resolvedTranslation.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase());
    });

    if (this.bottomDropdownAction?.showOnlyOnSearch) {
      this.bottomDropdownAction.translationKeyParams = { searchValue };
      this.bottomDropdownDisplayed = this.isBottomDropdownDisplayed(searchValue);
      this.searchValue = searchValue;
    }
  }

  private isBottomDropdownDisplayed(searchValue: string): boolean {
    if (this.bottomDropdownAction?.showOnlyForNotExistedValue) {
      return !!searchValue && !this.hasSearchEqualLabels(searchValue);
    }

    return !!searchValue;
  }

  hasSearchEqualLabels(searchValue: string): boolean {
    const hasEqualLabels = this.displayedData.some((item) => {
      const itemLabel = this.getDisplayValue(item);

      return itemLabel.toLocaleLowerCase() === searchValue.toLocaleLowerCase();
    });

    return hasEqualLabels;
  }

  getItemId(item: any): any | string | null {
    if (this.idSelector) {
      return this.idSelector(item);
    }
    return this.getDisplayValue(item);
  }

  getDisplayValue(item: any): string | null | any {
    if (item) {
      return this.displayValueSelector ? this.displayValueSelector(item) : item.toString();
    }
    return null;
  }

  getButtonDisplayValue(item: any): string | null | any {
    if (item) {
      return this.displayValueButton ? this.displayValueButton(item) : this.getDisplayValue(item);
    }

    return null;
  }

  getPinnedLabel(item: any): string | null {
    return item?.pinnedLabel;
  }

  getIsValueDisabled(item: any): boolean {
    if (item) {
      return this.disableValueSelector ? this.disableValueSelector(item) : !item;
    }
    return false;
  }

  searchFieldClick($event: MouseEvent): void {
    $event.stopPropagation();
    $event.preventDefault();
  }

  toggleDropdown(event: MouseEvent): void {
    if (this.isDropdownOpened) {
      this.close();
    } else {
      this.open();
    }
    event.stopPropagation();
  }

  open(): void {
    if (this.isDisabled) {
      return;
    }

    this.isDropdownOpened = true;
    this.dropdownOpen.emit();
  }

  close(): void {
    this.isDropdownOpened = false;
    this.searchField.setValue('');
  }

  handleSearchInput(): void {
    if (this.addingEnabled) {
      const searchValue = leaveOneSpace(this.searchField.value);
      this.isAddingShown = !!searchValue && !this.hasSearchEqualLabels(searchValue);
    }
  }

  async addClick(): Promise<void> {
    const searchValue = leaveOneSpace(this.searchField.value);
    this.addNew.emit(searchValue);

    if (this.addEntityCallback) {
      this.addOptionLoading$.next(true);
      await this.addEntityCallback(searchValue);
      this.searchField.setValue(searchValue);
      this.handleSearchInput();
      this.addOptionLoading$.next(false);
    }
  }

  @HostListener('window:mousedown', ['$event'])
  private windowClick(mouseEvent: MouseEvent): void {
    // Handles outside clicks to close dropdown
    if (
      this.shouldHandleOutsideClick &&
      this.isDropdownOpened &&
      !mouseEvent
        .composedPath()
        .some(
          (target) =>
            target === this.hostElementRef.nativeElement || target === this.openableElementElementRef.nativeElement,
        )
    ) {
      this.close();
    }
  }
}
